import React, { useState, useEffect } from 'react';
import { X, Palette } from 'lucide-react';

interface ColorPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onColorSelect?: (color: string, secondaryColor?: string) => void;
  currentColor?: string;
  currentSecondaryColor?: string;
}

export default function ColorPickerModal({
  isOpen,
  onClose,
  onColorSelect,
  currentColor = '#3b82f6',
  currentSecondaryColor
}: ColorPickerModalProps) {
  const [selectedColor, setSelectedColor] = useState(currentColor);
  const [selectedSecondaryColor, setSelectedSecondaryColor] = useState(currentSecondaryColor || '');

  // Update selected color when current color changes
  useEffect(() => {
    setSelectedColor(currentColor);
    setSelectedSecondaryColor(currentSecondaryColor || '');
  }, [currentColor, currentSecondaryColor]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Reset any state if needed when modal closes
    }
  }, [isOpen]);

  if (!isOpen) return null;



  const handleColorChange = (color: string, type: 'primary' | 'secondary' = 'primary') => {
    if (type === 'primary') {
      setSelectedColor(color);
    } else {
      setSelectedSecondaryColor(color);
    }
  };

  const handleSave = () => {
    if (onColorSelect) {
      onColorSelect(selectedColor, selectedSecondaryColor || undefined);
    }
  };

  const handleRemoveSecondaryColor = () => {
    setSelectedSecondaryColor('');
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-[100000] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-sm w-full max-h-[80vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col h-full">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Choose Colour</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            <div className="flex justify-center">
              <div className="flex-shrink-0 space-y-6">
                {/* Primary Color */}
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-3 text-center">Primary Colour</div>
                  <div className="relative">
                    <input
                      type="color"
                      value={selectedColor}
                      onChange={(e) => handleColorChange(e.target.value, 'primary')}
                      className="w-20 h-20 rounded-lg border-2 border-gray-300 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                    />
                  </div>
                  
                  <div className="mt-2 space-y-1">
                    <div className="text-xs text-gray-500 font-mono text-center">
                      {selectedColor.toUpperCase()}
                    </div>
                    <input
                      type="text"
                      value={selectedColor}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow typing and validate hex color
                        if (value.match(/^#[0-9A-Fa-f]{0,6}$/)) {
                          handleColorChange(value, 'primary');
                        }
                      }}
                      placeholder="#3b82f6"
                      className="w-full px-2 py-1 text-xs font-mono border border-gray-300 rounded text-center focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500"
                    />
                  </div>
                </div>

                {/* Secondary Color */}
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-3 text-center">Secondary Colour</div>
                  <div className="relative">
                    {selectedSecondaryColor ? (
                      <div className="relative">
                        <input
                          type="color"
                          value={selectedSecondaryColor}
                          onChange={(e) => handleColorChange(e.target.value, 'secondary')}
                          className="w-20 h-20 rounded-lg border-2 border-gray-300 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                        />
                        <button
                          onClick={handleRemoveSecondaryColor}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-xs font-bold shadow-sm"
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setSelectedSecondaryColor('#10b981')}
                        className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 shadow-sm hover:shadow-md hover:border-gray-400 transition-all relative overflow-hidden group bg-gray-50 hover:bg-gray-100"
                      >
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-center">
                            <Palette className="w-6 h-6 text-gray-400 group-hover:text-gray-600 mx-auto mb-1" />
                            <div className="text-xs text-gray-400 group-hover:text-gray-600">Add</div>
                          </div>
                        </div>
                      </button>
                    )}
                  </div>
                  
                  <div className="mt-2 space-y-1 h-12">
                    <div className="text-xs text-gray-500 font-mono text-center">
                      {selectedSecondaryColor ? selectedSecondaryColor.toUpperCase() : 'None'}
                    </div>
                    {selectedSecondaryColor && (
                      <input
                        type="text"
                        value={selectedSecondaryColor}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Allow typing and validate hex color
                          if (value.match(/^#[0-9A-Fa-f]{0,6}$/)) {
                            handleColorChange(value, 'secondary');
                          }
                        }}
                        placeholder="#10b981"
                        className="w-full px-2 py-1 text-xs font-mono border border-gray-300 rounded text-center focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Save/Cancel Buttons */}
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 text-sm font-medium text-white bg-gray-900 border border-transparent rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900"
              >
                Save Color
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 